import { useEffect, useState, useSyncExternalStore } from "react";
import type { Dict } from "@/i18n/uk.ts";
import { HttpError } from "./http.ts";

/** The current time, refreshed every `intervalMs` while `enabled`. */
export function useNow(enabled: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [enabled, intervalMs]);
  return now;
}

/** The value after it stopped changing for `delayMs`. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** True while the media query matches. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const media = window.matchMedia(query);
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** ≥1280px: sidebar, main column, and right panel side by side. */
export function useIsWide(): boolean {
  return useMediaQuery("(min-width: 1280px)");
}

/** <768px: one column with a segmented control. */
export function useIsMobile(): boolean {
  return useMediaQuery("(max-width: 767px)");
}

/** The message to show for a failed request: a translated one for a known error code, else the server's message. */
export function errorMessage(error: unknown, t: Dict): string {
  if (error instanceof HttpError) {
    if (error.status === 0) return t.errors.network;
    const known = error.code ? (t.errors.codes as Record<string, string | undefined>)[error.code] : undefined;
    return known ?? (error.message || t.errors.generic);
  }
  if (error instanceof Error && error.message) return error.message;
  return t.errors.generic;
}

export function isNotFound(error: unknown): boolean {
  return error instanceof HttpError && error.status === 404;
}
